import React from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { PriceText } from "@/components/produkter/bits";

/** What LOS Marine offers, as a reading list: trade → what it is → which departments and price-list groups carry it. */
export function AreasView({ catalog, onGroup, focus }) {
  const depts = Object.fromEntries(catalog.departments.map((d) => [d.code, d.name]));
  return (
    <div className="mt-6" data-testid="produkt-areas">
      {catalog.areas.map((a, i) => (
        <section key={a.key} id={`fag-${a.key}`} className={cn("border-t border-black/[0.06] py-6 first:border-t-0", focus === a.key && "bg-[hsl(var(--navy)/0.03)] -mx-4 px-4 rounded-[12px]")} data-testid={`produkt-area-${a.key}`}>
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <span className="font-mono text-[13px] text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="font-display text-[18px] font-semibold tracking-[-0.015em] text-[hsl(var(--navy))]">{a.label}</h3>
            {a.lines ? <span className="text-[12.5px] text-muted-foreground">{a.lines} prislistelinjer</span> : null}
          </div>
          <p className="mt-1.5 max-w-[720px] text-[14px] leading-relaxed text-foreground/80">{a.lead}</p>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-[13px]">
            {a.departments.length ? <span className="text-muted-foreground">Avdeling {a.departments.map((c) => <span key={c} className="ml-1.5 inline-flex items-center gap-1 text-foreground/80"><span className="font-mono text-[12px]">{c}</span>{depts[c]}</span>)}</span> : null}
            {a.rate_ids?.length ? <span className="text-muted-foreground">{a.rate_ids.length} timesatser</span> : null}
            {a.capabilities?.length ? <span className="text-muted-foreground">{a.capabilities.join(" · ")}</span> : null}
          </div>
          {a.groups.length ? (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`produkt-area-groups-${a.key}`}>
              {a.groups.map((gk) => { const g = catalog.groups.find((x) => x.key === gk); return g ? <button key={gk} type="button" onClick={() => onGroup(gk)} className="inline-flex h-8 items-center gap-1 rounded-full bg-black/[0.045] pl-3 pr-2 text-[13px] text-foreground/80 transition-colors hover:bg-black/[0.08]" data-testid={`produkt-area-group-${gk}`}>{g.label}<span className="font-mono text-[11.5px] text-muted-foreground">{g.count}</span><ChevronRight className="h-3.5 w-3.5 text-muted-foreground/60" /></button> : null; })}
            </div>
          ) : !a.capabilities?.length ? <p className="mt-2 text-[12.5px] text-muted-foreground">Ingen faste prislistelinjer – prises per forespørsel.</p> : null}
        </section>
      ))}
      <p className="mt-4 text-[12px] text-muted-foreground">{catalog.site?.note} Kilde: {catalog.site?.source}.</p>
    </div>
  );
}

/** Dock, slip and quays: the yard's hard numbers side by side, with the price-list lines that belong to each. */
export function FacilitiesView({ catalog, onOpen }) {
  const fac = catalog.facilities;
  const rows = [...new Set(fac.flatMap((f) => f.specs.map(([k]) => k)))];
  return (
    <div className="mt-6" data-testid="produkt-facilities">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] border-collapse text-[14px]">
          <thead>
            <tr className="text-left text-[11.5px] font-medium text-muted-foreground/70">
              <th className="py-2 pr-4 font-medium">Hoveddata</th>
              {fac.map((f) => <th key={f.key} className="py-2 pr-4 font-display text-[15px] font-semibold text-[hsl(var(--navy))]">{f.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((k) => (
              <tr key={k} className="border-t border-black/[0.06]" data-testid={`produkt-facility-row-${k}`}>
                <td className="py-2.5 pr-4 text-muted-foreground">{k}</td>
                {fac.map((f) => { const v = f.specs.find(([kk]) => kk === k)?.[1]; return <td key={f.key} className={cn("py-2.5 pr-4", !v && "text-muted-foreground/40")}>{v || "–"}</td>; })}
              </tr>
            ))}
            <tr className="border-t border-black/[0.08]" data-testid="produkt-facility-row-inout">
              <td className="py-2.5 pr-4 text-muted-foreground">Inn og ut, alle størrelser</td>
              {fac.map((f) => <td key={f.key} className="py-2.5 pr-4">{f.in_out ? <button type="button" onClick={() => onOpen(f.in_out.id)} className="underline-offset-[3px] hover:underline" data-testid={`produkt-facility-inout-${f.key}`}><PriceText line={{ ...f.in_out, unit: null }} /></button> : <span className="text-muted-foreground/40">–</span>}</td>)}
            </tr>
          </tbody>
        </table>
      </div>
      <h3 className="mt-8 text-[15px] font-semibold text-foreground">Døgnrater etter fartøyets lengde</h3>
      <div className="mt-2 grid gap-5 sm:grid-cols-2" data-testid="produkt-day-rates">
        {[["Dokk og slipp", fac[0]?.day_rates || []], ["Kai", fac[2]?.day_rates || []]].map(([label, lines]) => (
          <div key={label} className="rounded-[12px] border border-black/[0.06]">
            <div className="border-b border-black/[0.06] px-4 py-2.5 text-[13px] font-medium">{label}</div>
            {lines.map((l) => <button key={l.id} type="button" onClick={() => onOpen(l.id)} className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-[13.5px] transition-colors hover:bg-black/[0.02]" data-testid={`produkt-day-rate-${l.id}`}><span className="min-w-0 truncate">{l.description}</span><PriceText line={l} /></button>)}
          </div>
        ))}
      </div>
      <p className="mt-4 text-[12px] text-muted-foreground">Kapasiteter fra {catalog.site?.source} ({catalog.site?.fetched}). Priser fra {catalog.pricelist?.name} {catalog.pricelist?.valid_from}.</p>
    </div>
  );
}
